import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import loadScript from "load-script";
const GOOGLE_SDK_URL = "https://apis.google.com/js/api.js";

import { checkUserDriveAccess } from "Utils/AttachmentHelpers";

import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import DrivePermissionModal from "./DrivePermissionModal/DrivePermissionModal";
import { GOOGLE_APP_ID } from "Constants";
import I18nHOC from "UIComponents/I18nHOC";

class GooglePickerChooser extends React.PureComponent {
  static propTypes = {
    children: PropTypes.node,
    viewId: PropTypes.string,
    authImmediate: PropTypes.bool,
    origin: PropTypes.string,
    onChange: PropTypes.func,
    createPicker: PropTypes.func,
    multiselect: PropTypes.bool,
    navHidden: PropTypes.bool,
    disabled: PropTypes.bool,
    userType: PropTypes.string,
    shouldStopPropagation: PropTypes.bool,
  };

  static defaultProps = {
    onChange: () => {},
    viewId: "DOCS",
    authImmediate: false,
    multiselect: false,
    navHidden: false,
    disabled: false,
    shouldStopPropagation: true,
  };

  constructor(props) {
    super(props);
    this.state = { showDrivePermissionModal: false, accountId: "" };
    if (!document.getElementById("googlePickerChooserScript")) {
      loadScript(
        GOOGLE_SDK_URL,
        { attrs: { id: "googlePickerChooserScript" } },
        this.loadGapi
      );
    }
  }

  /** Following approach is used to wait for the picker to load
   *  without relying on it's callback method. More about this
   *  https://www.aworkinprogress.dev/turn-callback-into-promise-and-await */
  loadGapi = async () => {
    const load = new Promise((resolve, reject) => {
      try {
        window?.gapi?.load("picker", resolve);
      } catch (err) {
        reject(err);
      }
    });

    return load.then(() => window?.google?.picker).catch(() => null);
  };

  isGoogleReady() {
    return !!window?.gapi;
  }

  isGooglePickerReady() {
    return !!window.google?.picker;
  }

  checkIsSufficientDriveAccess = async ({ accessToken }) => {
    const hasAccess = await checkUserDriveAccess({
      accessToken,
    });
    return hasAccess;
  };

  successCallback = async ({ accessToken, id }) => {
    const hasSufficientDriveAccess = await this.checkIsSufficientDriveAccess({
      accessToken,
    });
    if (!hasSufficientDriveAccess) {
      this.setState({ showDrivePermissionModal: true, accountId: id });
      return;
    }
    if (!window?.google?.picker) {
      const picker = await this.loadGapi();
      if (!picker) return null;
    }
    let youtubeVideoView;

    let myDriveView;
    let starredView;
    let sharedWithMeView;

    let shareDriveView;
    const { updateAccessToken } = this.props;
    if (updateAccessToken) {
      updateAccessToken({ accessToken });
    }

    if (this.props.pickerType === "YoutubeVideo") {
      youtubeVideoView = new window.google.picker.VideoSearchView();
      youtubeVideoView.setSite("https://www.youtube.com");
    } else {
      const googleViewId = window.google.picker.ViewId[this.props.viewId];

      myDriveView = new window.google.picker.DocsView(googleViewId)
        .setMimeTypes(this.props.mimeTypes.join(","))
        .setIncludeFolders(true)
        .setParent("root")
        .setLabel("My Drive");

      starredView = new window.google.picker.DocsView(googleViewId)
        .setMimeTypes(this.props.mimeTypes.join(","))
        .setIncludeFolders(true)
        .setStarred(true)
        .setLabel("Starred");

      sharedWithMeView = new window.google.picker.DocsView(googleViewId)
        .setMimeTypes(this.props.mimeTypes.join(","))
        .setOwnedByMe(false)
        .setIncludeFolders(true);

      shareDriveView = new window.google.picker.DocsView(googleViewId)
        .setMimeTypes(this.props.mimeTypes.join(","))
        .setIncludeFolders(true)
        .setEnableDrives(true);
    }

    const picker = new window.google.picker.PickerBuilder()
      .setOAuthToken(accessToken)
      // .setDeveloperKey(this.props.developerKey)
      .setAppId(GOOGLE_APP_ID)
      .setCallback(this.props.onChange);

    if (this.props.pickerType === "YoutubeVideo") {
      picker.addView(youtubeVideoView);
    } else {
      picker.addView(myDriveView);
      picker.addView(starredView);
      picker.addView(sharedWithMeView);
    }

    if (this.props.origin) {
      picker.setOrigin(this.props.origin);
    }

    if (this.props.navHidden && !this.props.enableShareDrive) {
      picker.enableFeature(window.google.picker.Feature.NAV_HIDDEN);
    }

    if (this.props.enableShareDrive) {
      picker.addView(shareDriveView);
      picker.enableFeature(window.google.picker.Feature.SUPPORT_DRIVES);
    }

    if (this.props.multiselect) {
      picker.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
    }

    picker.build().setVisible(true);

    // add zindex to show picker on top
    setTimeout(() => {
      // Apply custom styles to the picker iframe
      const pickerDialogDom = document.querySelectorAll(".picker-dialog");
      if (_.size(pickerDialogDom) > 0) {
        pickerDialogDom.forEach(pickerDialog => {
          if (pickerDialog?.style) pickerDialog.style.zIndex = 1152;
        });

        const pickerBgDom = document.querySelectorAll(".picker-dialog-bg");
        if (_.size(pickerBgDom) > 0) {
          pickerBgDom.forEach(pickerBg => {
            if (pickerBg?.style) pickerBg.style.zIndex = 1151;
          });
        }
      }
    }, 50);
  };

  updateThirPartyAuthRef = ref => {
    const { setInnerGooglePickerRef } = this.props;
    // TODO: remove the wrappedInstance as it will be deprecated soon
    const authRef = _.get(ref, "refs.wrappedInstance", {});
    this.authPopUpRef = authRef;
    if (setInnerGooglePickerRef) {
      setInnerGooglePickerRef(authRef);
    }
  };

  onAuthClick = e => {
    const { shouldStopPropagation } = this.props;

    if (shouldStopPropagation) e.stopPropagation();

    if (
      !this.isGoogleReady() ||
      !this.isGooglePickerReady() ||
      this.props.disabled
    ) {
      return null;
    }

    if (this.authPopUpRef) {
      this.authPopUpRef.onAuthClick();
    }
  };

  onCloseModal = () => {
    this.setState({ showDrivePermissionModal: false, accountId: "" });
  };

  handleStopPropagation = e => {
    e?.stopPropagation?.();
  };

  render() {
    const { showDrivePermissionModal, accountId } = this.state;

    return (
      <>
        <div onClick={this.onAuthClick} className={this.props.classStyle}>
          <ThirdPartyAuth
            serviceType="GOOGLE"
            ref={this.updateThirPartyAuthRef}
            onSuccessCallBack={this.successCallback}
          />
          {this.props.children ? (
            this.props.children
          ) : (
            <button>{this.props.t("common:open_google_chooser")}</button>
          )}
        </div>
        {showDrivePermissionModal && (
          <div onClick={this.handleStopPropagation}>
            <DrivePermissionModal
              accountId={accountId}
              onAuthClick={this.onAuthClick}
              closeModal={this.onCloseModal}
            />
          </div>
        )}
      </>
    );
  }
}

export default I18nHOC({ resource: ["common"] })(GooglePickerChooser);
